// frontend/src/features/sales-rep/SalesRepPage.tsx
import React, { useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchSalesRepPerformance } from './services/salesRepAPI';
import { useFilterStore } from '../../stores/filterStore';
import { useAuthStore } from '../../stores/authStore';
import { DateNavigator, formatLocalDate } from '../../components/DateNavigator/DateNavigator';
import navStyles from '../../components/DateNavigator/DateNavigator.module.css';
import styles from './SalesRepPage.module.css';
import ModernDashboardHeader from '../../components/ModernDashboardHeader/ModernDashboardHeader';
import KPICard from '../../components/KPICard/KPICard';
import ReactivationChart from './components/ReactivationChart';
import GoalContributionChart from './components/GoalContributionChart';
import RevenueTreemap from './components/RevenueTreemap';
import TopSalesRepChart, { RankingMetric } from './components/TopSalesRepChart';
import RepActivityPieChart from './components/RepActivityPieChart';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';

type ViewMode = 'vendedores' | 'representantes';

const SalesRepPage: React.FC = () => {
    const { initialDate, finalDate, activePreset, setPreset, setDates } = useFilterStore();
    const isSupervisor = useAuthStore((state) => state.role === 'supervisor' || state.role === 'diretor');
    const [rankingMetric, setRankingMetric] = useState<RankingMetric>('faturamento');
    const [viewMode, setViewMode] = useState<ViewMode>('vendedores');
    const [periodo, setPeriodo] = useState<'mensal' | 'anual' | 'semanal' | 'custom'>('mensal');
    const [customInicio, setCustomInicio] = useState('');
    const [customFim, setCustomFim] = useState('');
    const [dataRef, setDataRef] = useState(() => formatLocalDate(new Date()));

    const applyFiscalMonth = useCallback((refDate: string) => {
        const base = new Date(refDate + 'T12:00:00');
        setDates(
            new Date(base.getFullYear(), base.getMonth() - 1, 26).toISOString().split('T')[0],
            new Date(base.getFullYear(), base.getMonth(), 25).toISOString().split('T')[0]
        );
    }, [setDates]);

    const applyPreset = useCallback((p: 'mensal' | 'anual' | 'semanal') => {
        if (p === 'mensal') applyFiscalMonth(dataRef || formatLocalDate(new Date()));
        else if (p === 'anual') setPreset('currentYear');
        else if (p === 'semanal') {
            const today = new Date();
            const day = today.getDay();
            const diff = today.getDate() - day + (day === 0 ? -6 : 1);
            setDates(
                new Date(today.getFullYear(), today.getMonth(), diff).toISOString().split('T')[0],
                new Date(today.getFullYear(), today.getMonth(), diff + 4).toISOString().split('T')[0]
            );
        }
    }, [dataRef, setDates, setPreset]);

    const formatDateLabel = (d: string) => new Date(d + 'T00:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' }).replace(/\./g, '');

    const getPeriodLabel = (): string => {
        if (periodo === 'mensal' && finalDate) {
            const date = new Date(finalDate + 'T00:00:00');
            return date.toLocaleDateString('pt-BR', { month: 'long' }).charAt(0).toUpperCase() + date.toLocaleDateString('pt-BR', { month: 'long' }).slice(1) + '/' + date.getFullYear().toString().slice(-2);
        }
        if (periodo === 'anual' && initialDate) return new Date(initialDate + 'T00:00:00').getFullYear().toString();
        return `${formatDateLabel(initialDate)} — ${formatDateLabel(finalDate)}`;
    };

    const { data: apiData, isLoading: loading, error: queryError } = useQuery({
        queryKey: ['repPerformance', initialDate, finalDate, activePreset],
        queryFn: async () => {
            const response = await fetchSalesRepPerformance(initialDate!, finalDate!, activePreset || undefined);
            return response.data;
        },
        enabled: !!initialDate && !!finalDate && isSupervisor,
        staleTime: 5 * 60 * 1000,
    });
    const error = queryError ? 'Falha ao carregar dados dos vendedores.' : null;

    const formatCurrency = (value: number) => {
        return value.toLocaleString('pt-BR', {
            style: 'currency',
            currency: 'BRL',
            notation: 'compact',
            compactDisplay: 'short'
        });
    };

    const getRankingData = () => {
        if (!apiData) return [];

        if (viewMode === 'representantes') {
            return apiData.topRepresentatives
                // 1. Filtra TELEVENDAS
                .filter(rep => !rep.nomeVendedor.toUpperCase().trim().startsWith('TELEVENDAS'))
                // 2. Formata os nomes
                .map(rep => ({
                    ...rep,
                    // AQUI: Salvamos o nome original em uma nova propriedade para o Tooltip ler
                    nomeCompleto: rep.nomeVendedor,
                    // E aqui truncamos para o Eixo Y (visualização)
                    nomeVendedor: rep.nomeVendedor.trim().split(' ').slice(0, 2).join(' ')
                }));
        }

        return apiData.topSalesReps;
    };

    const formatNumber = (value: number) => {
        return value.toLocaleString('pt-BR');
    };

    const showReactivation = apiData
        && (activePreset === 'currentMonth' || activePreset === 'lastMonth')
        && apiData.reactivationLeaders.length > 0;

    if (!isSupervisor) {
        return (
            <div className={styles.pageContainer}>
                <div className={styles.errorContainer}>
                    <h2>Acesso Negado</h2>
                    <p>Esta página é exclusiva para supervisores.</p>
                </div>
            </div>
        );
    }

    const displayRange = getPeriodLabel();

    return (
        <><style>{`.recharts-wrapper * { outline: none !important; } svg:focus { outline: none !important; } @keyframes pulse { 0%,100% { opacity: 1; } 50% { opacity: .5; } }`}</style>
        <div className={styles.pageContainer}>
            <ModernDashboardHeader>
                <div className={navStyles.periodContainer}>
                    {(['anual', 'mensal', 'semanal', 'custom'] as const).map((p) => (
                        <button key={p} onClick={() => { setPeriodo(p); if (p !== 'custom') applyPreset(p); }}
                            className={`${navStyles.periodButton} ${periodo === p ? navStyles.periodButtonActive : ''}`}
                        >{p === 'custom' ? 'Personalizado' : p}</button>
                    ))}
                </div>
                <DateNavigator label="" periodo={periodo} dataRef={dataRef} dataInicio={customInicio} dataFim={customFim}
                    displayRange={displayRange}
                    onChange={(vals: any) => {
                        if (vals.dataRef) {
                            setDataRef(vals.dataRef);
                            const base = new Date(vals.dataRef + 'T12:00:00');
                            if (periodo === 'mensal') applyFiscalMonth(vals.dataRef);
                            else if (periodo === 'anual') setDates(new Date(base.getFullYear(), 0, 1).toISOString().split('T')[0], new Date(base.getFullYear(), 11, 31).toISOString().split('T')[0]);
                            else if (periodo === 'semanal') { const day = base.getDay(); const diff = base.getDate() - day + (day === 0 ? -6 : 1); setDates(new Date(base.getFullYear(), base.getMonth(), diff).toISOString().split('T')[0], new Date(base.getFullYear(), base.getMonth(), diff + 4).toISOString().split('T')[0]); }
                        }
                        if (vals.dataInicio && vals.dataFim) { setCustomInicio(vals.dataInicio); setCustomFim(vals.dataFim); setDates(vals.dataInicio, vals.dataFim); setPeriodo('custom'); }
                    }}
                />
            </ModernDashboardHeader>

            {loading ? (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, padding: 0 }}>
                    {[1,2,3,4,5].map(i => (
                        <div key={i} style={{ height: 300, background: '#f1f5f9', borderRadius: 12, animation: 'pulse 2s ease infinite' }} />
                    ))}
                </div>
            ) : error ? (
                <p className={styles.errorText}>{error}</p>
            ) : !apiData ? (
                <p className={styles.noDataText}>Nenhum dado encontrado para o período selecionado.</p>
            ) : (
                <>
                    {/* KPIs Compact Filter Bar */}
                    <div className={styles.filterBar}>
                        <div className={styles.metaBadge}>
                            <PeopleAltIcon className={styles.metaIcon} />
                            <span className={styles.metaLabel}>Vendedores:</span>
                            <span className={styles.metaValue}>{formatNumber(apiData.kpis.totalVendedores)}</span>
                        </div>
                        <div className={styles.metaBadge}>
                            <MonetizationOnIcon className={styles.metaIcon} />
                            <span className={styles.metaLabel}>Faturamento:</span>
                            <span className={styles.metaValue}>{formatCurrency(apiData.kpis.faturamentoTotal)}</span>
                        </div>
                        <div className={styles.metaBadge}>
                            <TrendingUpIcon className={styles.metaIcon} />
                            <span className={styles.metaLabel}>Ticket Médio:</span>
                            <span className={styles.metaValue}>{formatCurrency(apiData.kpis.ticketMedio)}</span>
                        </div>
                        {showReactivation && (
                            <div className={styles.metaBadge}>
                                <CheckCircleOutlinedIcon className={styles.metaIcon} />
                                <span className={styles.metaLabel}>Reativados:</span>
                                <span className={styles.metaValue}>{formatNumber(apiData.kpis.totalReativacoes)}</span>
                            </div>
                        )}
                    </div>

                    <div className={styles.chartsGrid}>

                        {/* 1. Contribuição para Meta (Esquerda - 65%) */}
                        <div className={styles.chartWidget}>
                            <h2 className={styles.widgetTitle}>Realizado vs Meta</h2>
                            <div className={styles.chartContainer}>
                                {apiData.goalContribution.length > 0 ? (
                                    <GoalContributionChart data={apiData.goalContribution} />
                                ) : (
                                    <p className={styles.noDataText}>Nenhuma meta definida.</p>
                                )}
                            </div>
                        </div>

                        {/* 2. Ranking de Vendedores (Direita - 35%) */}
                        <div className={styles.chartWidget}>

                            {/* HEADER COMPLETO: Título + Abas + Filtro R$/Pares */}
                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '10px',
                                marginBottom: '15px'
                            }}>
                                {/* LINHA 1: Navbar de Visualização (Abas) */}
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    gap: '15px',
                                    borderBottom: '1px solid #eee',
                                    paddingBottom: '5px'
                                }}>
                                    <button
                                        onClick={() => setViewMode('vendedores')}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            borderBottom: viewMode === 'vendedores' ? '3px solid #1B2A49' : '3px solid transparent',
                                            color: viewMode === 'vendedores' ? '#1B2A49' : '#999',
                                            padding: '6px 12px',
                                            fontSize: '0.95rem',
                                            fontWeight: '600',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s'
                                        }}
                                    >
                                        Vendedores
                                    </button>
                                    <button
                                        onClick={() => setViewMode('representantes')}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            borderBottom: viewMode === 'representantes' ? '3px solid #1B2A49' : '3px solid transparent',
                                            color: viewMode === 'representantes' ? '#1B2A49' : '#999',
                                            padding: '6px 12px',
                                            fontSize: '0.95rem',
                                            fontWeight: '600',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s'
                                        }}
                                    >
                                        Representantes
                                    </button>
                                </div>

                                {/* LINHA 2: Título Dinâmico e Toggle R$/Pares */}
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center'
                                }}>
                                    <h2 className={styles.widgetTitle} style={{ margin: 0, fontSize: '1.1em' }}>
                                        Ranking por {viewMode === 'vendedores' ? 'Carteira' : 'Representante'}
                                    </h2>

                                    {/* Toggle R$ / Pares (Mantido igual) */}
                                    <div style={{
                                        display: 'flex',
                                        gap: '4px',
                                        backgroundColor: '#f5f5f5',
                                        padding: '4px',
                                        borderRadius: '6px'
                                    }}>
                                        <button
                                            onClick={() => setRankingMetric('faturamento')}
                                            style={{
                                                border: 'none',
                                                background: rankingMetric === 'faturamento' ? '#fff' : 'transparent',
                                                color: rankingMetric === 'faturamento' ? '#1B2A49' : '#666',
                                                padding: '4px 12px',
                                                borderRadius: '4px',
                                                cursor: 'pointer',
                                                fontWeight: rankingMetric === 'faturamento' ? 'bold' : '500',
                                                fontSize: '0.8rem',
                                                boxShadow: rankingMetric === 'faturamento' ? '0 1px 2px rgba(0,0,0,0.1)' : 'none',
                                                transition: 'all 0.2s'
                                            }}
                                        >
                                            R$
                                        </button>
                                        <button
                                            onClick={() => setRankingMetric('pares')}
                                            style={{
                                                border: 'none',
                                                background: rankingMetric === 'pares' ? '#fff' : 'transparent',
                                                color: rankingMetric === 'pares' ? '#dd1518' : '#666',
                                                padding: '4px 12px',
                                                borderRadius: '4px',
                                                cursor: 'pointer',
                                                fontWeight: rankingMetric === 'pares' ? 'bold' : '500',
                                                fontSize: '0.8rem',
                                                boxShadow: rankingMetric === 'pares' ? '0 1px 2px rgba(0,0,0,0.1)' : 'none',
                                                transition: 'all 0.2s'
                                            }}
                                        >
                                            Pares
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div className={styles.chartContainer}>
                                {getRankingData().length > 0 ? (
                                    <TopSalesRepChart
                                        data={getRankingData()}
                                        metric={rankingMetric}
                                    />
                                ) : (
                                    <p className={styles.noDataText}>Nenhum dado disponível.</p>
                                )}
                            </div>
                        </div>

                        {/* 3. Reativações (Esquerda - 65%) - Apenas se houver dados */}
                        {showReactivation && (
                            <div className={styles.chartWidget}>
                                <h2 className={styles.widgetTitle}>Líderes em Reativação de Clientes</h2>
                                <div className={styles.chartContainer}>
                                    <ReactivationChart data={apiData.reactivationLeaders} />
                                </div>
                            </div>
                        )}

                        {/* 3.5. Atividade dos Representantes - Pie Chart */}
                        <div className={styles.chartWidget}>
                            <h2 className={styles.widgetTitle}>Atividade dos Representantes</h2>
                            <div className={styles.chartContainer}>
                                {apiData.repActivity ? (
                                    <RepActivityPieChart data={apiData.repActivity} />
                                ) : (
                                    <p className={styles.noDataText}>Nenhum dado disponível.</p>
                                )}
                            </div>
                        </div>

                        {/* 4. Treemap */}
                        <div className={styles.chartWidget}>
                            <h2 className={styles.widgetTitle}>Distribuição de Faturamento (Share)</h2>
                            <div className={styles.chartContainer}>
                                {apiData.topSalesReps.length > 0 ? (
                                    <RevenueTreemap data={apiData.topSalesReps} />
                                ) : (
                                    <p className={styles.noDataText}>Nenhum dado disponível.</p>
                                )}
                            </div>
                        </div>

                    </div>
                </>
            )}
        </div>
        </>
    );
};

export default SalesRepPage;